import type { ChatWidgetDef } from '../types'
import { useState } from 'react'
import { useCallContext } from '../context/CallContext'
import { DMView } from './DMView'
import { GroupChannelView } from './GroupChannelView'
import { WhatsAppWidgetView } from './WhatsAppWidgetView'
import { PresenceDot } from './PresenceDot'
import { XIcon, ExternalLinkIcon, UserIcon, HashIcon, ChevronDownIcon, PhoneIcon, PaperPlaneIcon } from '../icons'

function ChatWidget({ widget, onClose, onExpand }: { widget: ChatWidgetDef; onClose: () => void; onExpand: () => void }) {
  const { type, id, name, avatar, presenceStatus, channelId, platform } = widget
  const [minimized, setMinimized] = useState(false)
  const call = useCallContext()

  const renderHeader = () => {
    if (type === 'dm') {
      return (
        <>
          <div className="chat-widget-avatar-wrap presence-avatar">
            {avatar ? (
              <img src={avatar} alt="" className="chat-widget-avatar" />
            ) : (
              <div className="chat-widget-avatar-placeholder"><UserIcon size={14} /></div>
            )}
            <PresenceDot status={presenceStatus || 'offline'} size={8} />
          </div>
          <span className="chat-widget-name">{name}</span>
        </>
      )
    }
    if (type === 'whatsapp') {
      return (
        <>
          <div className="chat-widget-avatar-wrap">
            {avatar ? (
              <img src={avatar} alt="" className="chat-widget-avatar" />
            ) : (
              <div className="chat-widget-avatar-placeholder">
                {platform === 'telegram' ? <PaperPlaneIcon size={14} /> : <PhoneIcon size={14} />}
              </div>
            )}
          </div>
          <span className="chat-widget-name">{name}</span>
        </>
      )
    }
    return (
      <>
        <HashIcon size={14} />
        <span className="chat-widget-name">{name}</span>
      </>
    )
  }

  const renderBody = () => {
    if (type === 'dm') return <DMView userId={id} />
    if (type === 'whatsapp') return <WhatsAppWidgetView channelId={channelId || ''} contactId={id} />
    return <GroupChannelView channelId={id} />
  }

  return (
    <div className={`chat-widget-container ${minimized ? 'minimized' : ''}`}>
      <div className="chat-widget-header" onClick={() => setMinimized(m => !m)}>
        <div className="chat-widget-header-info">
          {renderHeader()}
        </div>
        <div className="chat-widget-header-actions" onClick={e => e.stopPropagation()}>
          {type === 'dm' && (
            <button
              className="chat-widget-btn"
              onClick={() => { call.startCall(id, false); onExpand() }}
              title="Chamada de voz"
            >
              <PhoneIcon size={14} />
            </button>
          )}
          <button className="chat-widget-btn" onClick={onExpand} title="Abrir em tela cheia">
            <ExternalLinkIcon size={14} />
          </button>
          <button className="chat-widget-btn" onClick={() => setMinimized(m => !m)} title={minimized ? 'Expandir' : 'Minimizar'}>
            <ChevronDownIcon size={14} style={{ transform: minimized ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
          </button>
          <button className="chat-widget-btn" onClick={onClose} title="Fechar">
            <XIcon size={14} />
          </button>
        </div>
      </div>
      {!minimized && (
        <div className="chat-widget-body">
          {renderBody()}
        </div>
      )}
    </div>
  )
}

export function ChatWidgetManager({ widgets, onClose, onExpand }: { widgets: ChatWidgetDef[]; onClose: (type: string, id: string) => void; onExpand: (type: string, id: string) => void }) {
  if (!widgets || widgets.length === 0) return null

  return (
    <div className="chat-widget-stack">
      {widgets.map((w) => (
        <ChatWidget
          key={`${w.type}-${w.id}`}
          widget={w}
          onClose={() => onClose(w.type, w.id)}
          onExpand={() => onExpand(w.type, w.id)}
        />
      ))}
    </div>
  )
}
